import { io, Socket } from 'socket.io-client';

// Socket.IO must connect to the API origin only (no path). If VITE_API_URL includes /api,
// the client would otherwise treat it as the namespace and the server would reject with "Invalid namespace".
export function getSocketBaseUrl(): string {
  const url = import.meta.env.VITE_API_URL || 'http://localhost:3000';
  // Relative URL (e.g. /api from Docker build) → use current origin only
  if (typeof url === 'string' && url.startsWith('/')) {
    if (typeof window !== 'undefined') {
      return window.location.origin;
    }
    return url.replace(/^\/api\/?$/, '').replace(/\/+$/, '') || '/';
  }
  try {
    const u = new URL(url);
    u.pathname = '';
    u.search = '';
    u.hash = '';
    const base = u.toString().replace(/\/$/, '');
    return base;
  } catch {
    return url.replace(/\/api\/?$/, '').replace(/\/+$/, '') || url;
  }
}

class SocketService {
  private socket: Socket | null = null;
  private isConnected = false;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 1000; // Start with 1 second

  // Event listeners
  private notificationListeners: ((notification: any) => void)[] = [];
  private countListeners: ((count: number) => void)[] = [];
  private authListeners: ((success: boolean, message?: string) => void)[] = [];

  connect(token: string, agencySlug: string): Promise<boolean> {
    return new Promise((resolve, reject) => {
      try {
        // Disconnect existing connection if any
        if (this.socket) {
          this.socket.disconnect();
        }

        // Create new connection
        this.socket = io(getSocketBaseUrl(), {
          path: '/socket.io',
          transports: ['websocket', 'polling'],
          autoConnect: true,
          reconnection: true,
          reconnectionAttempts: this.maxReconnectAttempts,
          reconnectionDelay: this.reconnectDelay,
        });

        // Connection events
        this.socket.on('connect', () => {
          console.log('✅ Socket.IO connected');
          this.isConnected = true;
          this.reconnectAttempts = 0;
          this.reconnectDelay = 1000; // Reset delay
          
          // Authenticate immediately after connection
          this.authenticate(token, agencySlug);
        });

        this.socket.on('disconnect', (reason) => {
          console.log('❌ Socket.IO disconnected:', reason);
          this.isConnected = false;
          
          if (reason === 'io server disconnect') {
            // Server disconnected us, try to reconnect
            this.socket?.connect();
          }
        });

        this.socket.on('connect_error', (error) => {
          console.error('❌ Socket.IO connection error:', error);
          this.isConnected = false;
          reject(error);
        });

        // Authentication events
        this.socket.on('authenticated', (data) => {
          console.log('✅ Socket.IO authenticated:', data);
          this.notifyAuthListeners(true, 'Successfully authenticated');
          resolve(true);
        });

        this.socket.on('auth_error', (data) => {
          console.error('❌ Socket.IO authentication error:', data);
          this.notifyAuthListeners(false, data.message);
          reject(new Error(data.message));
        });

        // Notification events
        this.socket.on('notification', (data) => {
          console.log('📨 Received real-time notification:', data);
          // Backend sends { type: 'new_notification', data: notification }
          if (data.type === 'new_notification' && data.data) {
            this.notifyNotificationListeners(data.data);
          } else {
            // Fallback for direct notification object
            this.notifyNotificationListeners(data);
          }
        });

        this.socket.on('notification_count', (data) => {
          console.log('📊 Received notification count update:', data);
          // Backend sends { type: 'count_update', data: { unreadCount: number } }
          if (data.type === 'count_update' && data.data?.unreadCount !== undefined) {
            this.notifyCountListeners(data.data.unreadCount);
          } else if (typeof data === 'number') {
            // Fallback for direct count number
            this.notifyCountListeners(data);
          }
        });

        // Reconnection events
        this.socket.on('reconnect_attempt', (attemptNumber) => {
          console.log(`🔄 Socket.IO reconnection attempt ${attemptNumber}`);
          this.reconnectAttempts = attemptNumber;
          
          // Exponential backoff
          this.reconnectDelay = Math.min(1000 * Math.pow(2, attemptNumber - 1), 30000);
        });

        this.socket.on('reconnect', (attemptNumber) => {
          console.log(`✅ Socket.IO reconnected after ${attemptNumber} attempts`);
          this.isConnected = true;
          this.reconnectAttempts = 0;
          
          // Re-authenticate after reconnection
          this.authenticate(token, agencySlug);
        });

        this.socket.on('reconnect_failed', () => {
          console.error('❌ Socket.IO reconnection failed after all attempts');
          this.isConnected = false;
          reject(new Error('Failed to reconnect after all attempts'));
        });

      } catch (error) {
        console.error('❌ Error creating Socket.IO connection:', error);
        reject(error);
      }
    });
  }

  private authenticate(token: string, agencySlug: string) {
    if (this.socket && this.isConnected) {
      console.log('🔐 Authenticating Socket.IO connection...');
      this.socket.emit('authenticate', { token, agencySlug });
    }
  }

  disconnect() {
    if (this.socket) {
      console.log('🔌 Disconnecting Socket.IO...');
      this.socket.disconnect();
      this.socket = null;
      this.isConnected = false;
    }
  }

  // Event listener management
  onNotification(callback: (notification: any) => void) {
    this.notificationListeners.push(callback);
    return () => {
      this.notificationListeners = this.notificationListeners.filter(cb => cb !== callback);
    };
  }

  onNotificationCount(callback: (count: number) => void) {
    this.countListeners.push(callback);
    return () => {
      this.countListeners = this.countListeners.filter(cb => cb !== callback);
    };
  }

  onAuth(callback: (success: boolean, message?: string) => void) {
    this.authListeners.push(callback);
    return () => {
      this.authListeners = this.authListeners.filter(cb => cb !== callback);
    };
  }

  // Get socket instance for custom event listeners
  getSocket(): Socket | null {
    return this.socket;
  }

  // Listen for custom events
  on(event: string, callback: (data: any) => void) {
    if (this.socket) {
      this.socket.on(event, callback);
      return () => {
        if (this.socket) {
          this.socket.off(event, callback);
        }
      };
    }
    return () => {};
  }

  private notifyNotificationListeners(notification: any) {
    this.notificationListeners.forEach(callback => {
      try {
        callback(notification);
      } catch (error) {
        console.error('Error in notification listener:', error);
      }
    });
  }

  private notifyCountListeners(count: number) {
    this.countListeners.forEach(callback => {
      try {
        callback(count);
      } catch (error) {
        console.error('Error in count listener:', error);
      }
    });
  }

  private notifyAuthListeners(success: boolean, message?: string) {
    this.authListeners.forEach(callback => {
      try {
        callback(success, message);
      } catch (error) {
        console.error('Error in auth listener:', error);
      }
    });
  }

  // Utility methods
  isSocketConnected(): boolean {
    return this.isConnected && this.socket?.connected === true;
  }

  getConnectionState(): string {
    if (!this.socket) return 'disconnected';
    return this.socket.connected ? 'connected' : 'disconnected';
  }
}

// Export singleton instance
export const socketService = new SocketService();
export default socketService; 